import assert from 'assert';

import * as ArchiveActions from 'archive/ArchiveActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ACTION_ARCHIVE_ROWS} from 'notebook/store/VersioningConstants';
import makeArchiveRowActionFunction from 'notebook/structured-tables/action-functions/makeArchiveRowActionFunction';
import {archiveRows, unarchiveRows} from 'notebook/structured-tables/ActionFunctionDAO';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/util/PerformActionFunctions';
import {openArchiveModalForProcessItems, ArchivableProcessTask} from 'processes/archive/archiveUtil';
import {BulkArchiveResponse} from 'processes/dao/ProcessManagementDAO';
import {ProcessTableLocation} from 'processes/ProcessConstants';

import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
  getTaskSchema,
} from '../ProcessTaskStructuredTableAdapter';

function updateTaskTable(
  actionFunctionParams: AxisContextMenuActionFunctionParameters<
    ProcessTaskStructuredTableMetadata,
    ProcessTaskStructuredTableRowMetadata
  >,
  itemIds: Array<string>,
  newArchivePurpose: string | null
): void {
  const adapter = actionFunctionParams.structuredTableAdapter as ProcessTaskStructuredTableAdapter;
  const lastSyncedArchivePurposeByRow = actionFunctionParams.structuredTable.rows.map((row) => {
    const taskIdForRow = adapter.getTaskId(actionFunctionParams.structuredTable.columns, row);
    return taskIdForRow && itemIds.includes(taskIdForRow) ? newArchivePurpose : undefined;
  });
  const updatedCells = actionFunctionParams.structuredTable.rows.map((row) => row.cells);
  actionFunctionParams.updateHandler({
    updatedCells,
    columnUpdates: {},
    lastSyncedArchivePurposeByRow,
  });
}

export const ARCHIVE_TASK_ROW_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: true,
  getSelectedItems: (
    structuredTable: ProcessTaskStructuredTable,
    selectedRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>>,
    adapter: ProcessTaskStructuredTableAdapter
  ): Array<{id: string; rowId: string; modelType: ModelTypeConstants}> => {
    const taskItems: Array<{id: string; rowId: string; modelType: ModelTypeConstants}> = [];
    for (const row of selectedRows) {
      const taskId = adapter.getTaskId(structuredTable.columns, row);
      if (taskId) {
        taskItems.push({id: taskId, rowId: row.rowId, modelType: ModelTypeConstants.PROCESS_TASK});
      }
    }
    return taskItems;
  },
  onSelectForArchive: (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessTaskStructuredTableMetadata,
      ProcessTaskStructuredTableRowMetadata
    >,
    items: Array<{id: string; rowId: string; modelType: ModelTypeConstants}>
  ): void => {
    const itemIdToRowId: {[id: string]: string} = {};
    for (const item of items) {
      itemIdToRowId[item.id] = item.rowId;
    }

    const structuredTable = actionFunctionParams.structuredTable;
    const adapter = actionFunctionParams.structuredTableAdapter as ProcessTaskStructuredTableAdapter;

    const taskSchema = getTaskSchema(structuredTable.schemas);
    assert(taskSchema != null, 'Task schema not found in structured table');

    const taskDataForArchive: Array<ArchivableProcessTask> = items.map((item) => {
      const row = structuredTable.rows.find((row) => row.rowId === item.rowId);

      assert(row != null, `Row for ${item.id} not found in structured table`);

      let taskStatus;

      // Request v2 documents don't have a status column. But since you are creating a task in this context, we can
      // just use the initial status from the schema.
      if (structuredTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT) {
        taskStatus = taskSchema.statusLifecycle?.initialStatus;
      } else {
        taskStatus = adapter.getStatus(structuredTable.columns, row);
      }

      assert(taskStatus != null, 'Task status not found in structured table or schema');

      const taskHumanId = adapter.getTaskId(structuredTable.columns, row);
      assert(taskHumanId != null, 'Task human id not found in structured table');

      return {
        __typename: 'ProcessTaskNode',
        id: item.id,
        name: taskHumanId,
        schema: taskSchema,
        status: taskStatus,
      };
    });

    if (actionFunctionParams.entryOrTemplateId == null) {
      openArchiveModalForProcessItems({
        itemType: ModelTypeConstants.PROCESS_TASK,
        items: taskDataForArchive,
        afterArchive: (archiveResponse: BulkArchiveResponse, purpose: string) => {
          updateTaskTable(actionFunctionParams, archiveResponse.archivedItemIds, purpose);
        },
      });
    } else {
      const customArchive = (itemIds: Array<string>, purpose: string): void => {
        performServerSideTableWriteActionWithAudits({
          entryId: actionFunctionParams.entryOrTemplateId!,
          loadingScreenMessage: 'Archiving rows...',
          autoSaverAction: ACTION_ARCHIVE_ROWS,
          doPerformAction: async (_?, __?, dataModsHeader?) => {
            await archiveRows(
              actionFunctionParams.entryOrTemplateId!,
              actionFunctionParams.structuredTable,
              actionFunctionParams.computedTable,
              actionFunctionParams.actionFunctionContext,
              itemIds.map((id) => ({
                id,
                modelType: ModelTypeConstants.PROCESS_TASK,
                rowId: itemIdToRowId[id],
              })),
              purpose,
              undefined,
              dataModsHeader
            );
            return {resultType: ActionResultType.SUCCESS, responseData: null};
          },
          analyticsActionForPerfTracking: 'archive_process_task_structured_table_rows',
          structuredTables: [actionFunctionParams.structuredTable],
          structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
          computedTables: [actionFunctionParams.computedTable],
          tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
        });
      };

      openArchiveModalForProcessItems({
        itemType: ModelTypeConstants.PROCESS_TASK,
        items: taskDataForArchive,
        customArchive,
      });
    }
  },
  onSelectForUnarchive: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessTaskStructuredTableMetadata,
      ProcessTaskStructuredTableRowMetadata
    >,
    items: Array<{id: string; modelType: ModelTypeConstants}>
  ): Promise<void> => {
    if (actionFunctionParams.entryOrTemplateId == null) {
      const itemIdsForUnarchive = items.map(({id}) => id);
      await ArchiveActions.bulkUnarchiveProcessManagement(
        ModelTypeConstants.PROCESS_TASK,
        itemIdsForUnarchive
      );
      updateTaskTable(actionFunctionParams, itemIdsForUnarchive, null);
    } else {
      performServerSideTableWriteActionWithAudits({
        entryId: actionFunctionParams.entryOrTemplateId,
        loadingScreenMessage: 'Unarchiving rows...',
        autoSaverAction: ACTION_ARCHIVE_ROWS,
        doPerformAction: async (_?, __?, dataModsHeader?) => {
          await unarchiveRows(
            actionFunctionParams.entryOrTemplateId!,
            actionFunctionParams.structuredTable,
            actionFunctionParams.computedTable,
            actionFunctionParams.actionFunctionContext,
            items,
            dataModsHeader
          );
          return {resultType: ActionResultType.SUCCESS, responseData: null};
        },
        analyticsActionForPerfTracking: 'unarchive_process_task_structured_table_rows',
        structuredTables: [actionFunctionParams.structuredTable],
        structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
        computedTables: [actionFunctionParams.computedTable],
        tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
      });
    }
  },
};

export default makeArchiveRowActionFunction<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
>(ARCHIVE_TASK_ROW_ACTION_FUNCTION_CONFIG);
